import { Component, OnInit, AfterViewInit, ViewChild, ElementRef, HostListener } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { of } from 'rxjs';
import { StorageService } from '@services';
import { LearnApi, AcademicProfileResume, UserProvider, ContentProvider, CompanyProvider } from '@lighty';
import { environment } from '@env/environment';
import { GrikySkillsService } from '../../modules/griky-skills/services/griky-skills.service';
import { GrikyUserSkill, GrikyAchievement, GrikyAchievementsResult, GapAnalysisResult } from '../../modules/griky-skills/griky-skills.types';

/**
 * Perfil PÚBLICO (URL con :slug, sin sesión).
 *
 * Usa el MISMO diseño que academy/public (AcademyPublicPageComponent) pero como
 * es público NO muestra controles de edición (isOwner siempre false: sin lápiz,
 * sin "+ Agregar", sin "Editar/Importar").
 *
 * Los datos vienen de getPublicProfile(token). Mientras el backend no devuelva
 * todo lo que el diseño necesita, se usan placeholders para que la vista se vea
 * completa. Reemplazar por datos reales cuando el endpoint público los provea.
 */
@Component({
  selector: 'app-public-profile',
  templateUrl: './public-profile.component.html',
  styleUrls: ['./public-profile.component.scss'],
})
export class PublicProfileComponent implements OnInit, AfterViewInit {

  @ViewChild('bioText', { static: false }) bioText?: ElementRef<HTMLParagraphElement>;

  // Perfil público NO editable
  public readonly isOwner = false;

  /** Franja sticky con nombre + Compartir: solo visible al hacer scroll hacia abajo (pasado el header grande). */
  public showStickyNameBar = false;
  @HostListener('window:scroll')
  onWindowScroll(): void {
    this.showStickyNameBar = window.scrollY > 260;
  }

  // ===== Sesión (decide qué navbar mostrar) =====
  public sessionMe: any = null;
  public sessionCompany: any = null;
  public sessionAccess: any = null;
  /** Id del dueño de ESTE perfil (viene del endpoint público). */
  private profileOwnerId: any = null;
  /**
   * Id de la company DUEÑA de este perfil — NO necesariamente la company activa
   * en la sesión (sessionCompany). El usuario puede tener seleccionada otra
   * company al entrar a esta URL, y las llamadas autenticadas (skills,
   * experiencia, logros…) deben pedirse con la company del PERFIL, o el backend
   * las scopea a la company equivocada y responden vacías aunque el endpoint
   * público sí tenga datos para la company correcta.
   */
  private ownerCompanyId: number | null = null;

  /** Hay sesión activa. */
  get isLoggedIn(): boolean {
    return !!(this.sessionMe && this.sessionMe.id);
  }

  /**
   * El que mira es EL MISMO dueño del perfil y está logueado → navbar de
   * plataforma (msc-header). Si no hay sesión, o es otra persona logueada,
   * se muestra el navbar público.
   */
  get isOwnerViewing(): boolean {
    return this.isLoggedIn && this.profileOwnerId != null && this.sessionMe.id === this.profileOwnerId;
  }

  /**
   * msc-header asume `access.roles`/`access.permissions` siempre presentes (no
   * los valida) y explota si se monta con `sessionAccess` incompleto. En esta
   * ruta ('profile/:slug') no hay CompanyResolver, así que sessionAccess puede
   * llegar null/parcial hasta que refreshOwnCompanyPermissions() responda. Se
   * muestra el navbar público mientras tanto para no romper la vista; en cuanto
   * sessionAccess trae roles+permissions, cambia al navbar autenticado.
   */
  get showAuthenticatedNavbar(): boolean {
    return this.isOwnerViewing && !!this.sessionAccess?.roles && !!this.sessionAccess?.permissions;
  }

  public company: any = { name: 'Griky', slug: '', experience: {} };
  /**
   * Módulo Griky Skills habilitado para la company DUEÑA de este perfil — no la
   * de quien mira. Se deriva de si el endpoint público realmente mandó datos de
   * GrikySkills (ver mapPublicProfile), nunca de sessionCompany: quien visita el
   * link puede no tener sesión, o estar logueado en una company distinta.
   */
  public grikySkillsEnabled = false;
  private gapAnalysis: GapAnalysisResult | null = null;
  // Datos reales: de la sesión (si hay) o de getPublicProfile(token). El avatar,
  // rol y "Sobre mí" salen de la misma lógica que academy/public (me.picture, etc.).
  public me: any = { name: '', firstname: '', lastname: '', picture: '', headline: '', biography: '', city: '' };

  // Biografía "Sobre mí": clamp a 4 líneas con "Ver más / Ver menos"
  public bioExpanded: boolean = false;
  public bioOverflowing: boolean = false;

  public currentYear: number = new Date().getFullYear();

  // Pestañas internas de "Mi perfil profesional" (mismo patrón que academy-page)
  public profileSubTab: 'resumen' | 'experience' | 'education' | 'certifications' | 'skills' = 'resumen';
  public setProfileSubTab(tab: 'resumen' | 'experience' | 'education' | 'certifications' | 'skills'): void {
    this.profileSubTab = tab;
  }

  /** Cantidad de sub-pestañas de "Mi perfil profesional" con contenido real (sin contar "Resumen"). */
  public get professionalSubTabsCount(): number {
    let count = 0;
    if (this.experiences.length) { count++; }
    if (this.education.length) { count++; }
    if (this.grikySkillsEnabled && this.acquiredSkills.length) { count++; }
    return count;
  }

  /** True si solo hay una sub-pestaña con contenido (no hay "Resumen" entre qué elegir). */
  public get isSingleProfessionalSubTab(): boolean {
    return this.professionalSubTabsCount < 2;
  }

  /** True si hay exactamente 2 secciones con contenido: se muestran ambas apiladas por completo, sin pestañas. */
  public get isStackedProfessionalMode(): boolean {
    return this.professionalSubTabsCount === 2;
  }

  /**
   * Sub-pestaña efectivamente activa: "Resumen" solo se habilita con 3+ secciones; con menos, no es una opción
   * válida. Se respeta cualquier sub-pestaña real que el usuario haya elegido (para poder navegar entre ellas);
   * solo se redirige lejos de 'resumen' cuando esta no está habilitada.
   */
  public get effectiveProfileSubTab(): string {
    if (this.professionalSubTabsCount >= 3 || this.profileSubTab !== 'resumen') { return this.profileSubTab; }
    if (this.experiences.length) { return 'experience'; }
    if (this.education.length) { return 'education'; }
    if (this.grikySkillsEnabled && this.acquiredSkills.length) { return 'skills'; }
    return this.profileSubTab;
  }

  /** Clave de traducción para el título de la card "Mi perfil profesional" cuando solo hay una sub-pestaña con contenido: el nombre de esa única sección, en vez del genérico "Perfil profesional". */
  public get singleProfessionalSubTabTitleKey(): string {
    switch (this.effectiveProfileSubTab) {
      case 'experience': return 'words.work-experience';
      case 'education': return 'words.education';
      case 'skills': return 'words.skills';
      default: return 'words.professional-profile';
    }
  }

  // Paginación "ver más" (de 5 en 5) de las listas completas
  public experienceVisibleCount = 5;
  public educationVisibleCount = 5;
  public externalCertsVisibleCount = 5;
  public showMoreExperience(): void { this.experienceVisibleCount += 5; }
  public showMoreEducation(): void { this.educationVisibleCount += 5; }
  public showMoreExternalCerts(): void { this.externalCertsVisibleCount += 5; }

  // Pestañas de navegación de la página (filtran qué sección se muestra)
  public activeSection: 'resumen' | 'about' | 'skills' | 'credentials' | 'trajectory' | 'recognitions' = 'resumen';
  public setActiveSection(section: 'resumen' | 'about' | 'skills' | 'credentials' | 'trajectory' | 'recognitions'): void {
    this.activeSection = section;
  }

  /** Cantidad de pestañas con contenido real (sin contar "Resumen"). Si hay menos de 2, "Resumen" se oculta. */
  public get availableProfileSectionsCount(): number {
    let count = 0;
    if (this.me?.biography) { count++; }
    if (this.experiences.length || this.education.length || (this.grikySkillsEnabled && this.acquiredSkills.length > 0)) { count++; }
    if (this.certificates.length > 0 || this.externalCerts.length > 0) { count++; }
    if (this.grikySkillsEnabled && this.unlockedAchievements.length > 0) { count++; }
    return count;
  }

  /** True si solo hay una pestaña con contenido real (no hay "Resumen" para elegir entre varias). */
  public get isSingleSectionMode(): boolean {
    return this.availableProfileSectionsCount < 2;
  }

  /** Color del texto/subrayado de la pestaña activa: primary cuando es la única pestaña, navy cuando hay varias. */
  public get activeTabColor(): string {
    return this.isSingleSectionMode ? 'var(--v2-color-primary-500)' : 'rgb(34, 48, 76)';
  }

  /**
   * Sección efectivamente activa: "Resumen" solo se habilita con 3+ secciones; con menos, no es una opción
   * válida. Se respeta cualquier pestaña real que el usuario haya elegido (para poder navegar entre ellas);
   * solo se redirige lejos de 'resumen' cuando esta no está habilitada.
   */
  public get effectiveActiveSection(): string {
    if (this.availableProfileSectionsCount >= 3 || this.activeSection !== 'resumen') { return this.activeSection; }
    if (this.me?.biography) { return 'about'; }
    if (this.experiences.length || this.education.length || (this.grikySkillsEnabled && this.acquiredSkills.length > 0)) { return 'trajectory'; }
    if (this.certificates.length > 0 || this.externalCerts.length > 0) { return 'credentials'; }
    if (this.grikySkillsEnabled && this.unlockedAchievements.length > 0) { return 'recognitions'; }
    return this.activeSection;
  }

  // Contadores "Mi contenido" (datos reales, MISMA lógica que academy: getContents por progreso)
  public statusCounts = { completed: 0, inProgress: 0, notStarted: 0, total: 0 };
  private allContents: any[] = [];

  // Perfil académico + certificados obtenidos (datos reales, igual que academy/public)
  public academicProfile: AcademicProfileResume | null = null;
  public certificates: any[] = [];

  // Habilidades adquiridas (datos reales)
  public acquiredSkills: GrikyUserSkill[] = [];

  // Logros (datos reales) + nivel/XP
  public achievements: GrikyAchievement[] = [];
  public userLevel: number | null = null; // real desde getAchievements / getPublicProfile
  public userXp: number = 0;
  public streak: number = 0; // días de racha (del skill; 0 si el backend no lo provee)

  // "Miembro desde / activo hace..." — solo si el endpoint público los provee (mapPublicProfile).
  // Sin fabricar fechas: si el backend no los envía, se ocultan en el template.
  public memberSince: string | null = null;
  public lastActiveLabel: string | null = null;

  /** "Miembro desde": mes y año (ej. "abr 2023"); si no hay mes disponible, solo el año. */
  private formatMemberSince(dateStr: string): string {
    const date = new Date(String(dateStr).replace(' ', 'T'));
    if (isNaN(date.getTime())) { return String(dateStr); }
    return date.toLocaleDateString('es-ES', { month: 'short', year: 'numeric' });
  }

  private formatRelativeTime(dateStr: string): string {
    const date = new Date(String(dateStr).replace(' ', 'T'));
    const diffMs = Date.now() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays <= 0) { return 'hoy'; }
    if (diffDays === 1) { return 'hace 1 día'; }
    if (diffDays < 30) { return `hace ${diffDays} días`; }
    const diffMonths = Math.floor(diffDays / 30);
    if (diffMonths < 12) { return diffMonths === 1 ? 'hace 1 mes' : `hace ${diffMonths} meses`; }
    const diffYears = Math.floor(diffMonths / 12);
    return diffYears === 1 ? 'hace 1 año' : `hace ${diffYears} años`;
  }

  // ===== Perfil profesional =====
  // Experiencia y Certificaciones: SIN datos (no hay lógica) → empty state.
  public experiences: Array<{ title: string; period: string; current?: boolean; logo?: string; startYear?: number }> = [];

  // Educación: SIN datos por defecto. Si viene vacía, la sección se oculta (no
  // hay empty state en el perfil público) y el contenido de abajo sube.
  public education: Array<{ university: string; career: string; period: string; startYear?: number }> = [];

  public externalCerts: Array<{ title: string; org: string; date: string; logo?: string; issueYear?: number }> = [];

  /** Experiencia ordenada por año (más reciente primero; "Actual" siempre de primero). */
  public get sortedExperiences(): typeof this.experiences {
    return [...this.experiences].sort((a, b) => {
      if (a.current && !b.current) { return -1; }
      if (!a.current && b.current) { return 1; }
      return (b.startYear || 0) - (a.startYear || 0);
    });
  }

  /** Educación ordenada por año (más reciente primero). */
  public get sortedEducation(): typeof this.education {
    return [...this.education].sort((a, b) => (b.startYear || 0) - (a.startYear || 0));
  }

  /** Certificaciones ordenadas por año (más reciente primero). */
  public get sortedExternalCerts(): typeof this.externalCerts {
    return [...this.externalCerts].sort((a, b) => (b.issueYear || 0) - (a.issueYear || 0));
  }

  constructor(
    private route: ActivatedRoute,
    private grikySkillsService: GrikySkillsService,
    private storageService: StorageService,
    private learnApi: LearnApi,
    private userProvider: UserProvider,
    private contentProvider: ContentProvider,
    private companyProvider: CompanyProvider
  ) {
    this.sessionMe = this.storageService.get('me');
    this.sessionCompany = this.storageService.get('company');
    this.sessionAccess = this.storageService.get('access');
    // Con sesión, el perfil base son los datos reales del usuario (foto, rol, bio…)
    if (this.sessionMe) {
      this.me = this.sessionMe;
    }
    if (this.sessionCompany) {
      this.company = this.sessionCompany;
    }
  }

  ngOnInit(): void {
    this.buildYears();
    const slug = this.route.snapshot.paramMap.get('slug') || '';

    // `me` es en memoria (StorageService); en carga directa/recarga puede estar
    // vacío. Resolvemos sessionMe PRIMERO (de storage o de /me) para que
    // isOwnerViewing quede correcto antes de decidir si traer datos autenticados.
    const sessionMe$ = (!this.sessionMe || !this.sessionMe.id)
      ? this.userProvider.me()
      : of(this.sessionMe);

    sessionMe$.subscribe({
      next: (me: any) => {
        if (me && (!this.sessionMe || !this.sessionMe.id)) {
          this.sessionMe = me;
          this.me = me;
          this.checkBioOverflow();
        }
      },
      // Visitante sin sesión: error y complete son mutuamente excluyentes en un
      // Observable, así que hay que disparar la carga del perfil público desde
      // AMBOS casos, o un visitante anónimo (el caso normal de una URL pública)
      // nunca vería nada.
      error: () => { this.fetchPublicProfile(slug); },
      complete: () => { this.fetchPublicProfile(slug); }
    });
  }

  private fetchPublicProfile(slug: string): void {
    if (!slug) { return; }
    this.learnApi.getPublicProfile(slug).subscribe({
      next: (data) => {
        this.mapPublicProfile(data);
        // El contenido del perfil (pestañas, tarjetas) ya quedó lleno con los
        // datos públicos de mapPublicProfile, sin depender de sesión. La ÚNICA
        // cosa que debe verificarse con el usuario logueado es cuál navbar
        // mostrar (msc-header del dueño vs. nav público), y esa verificación
        // por slug es más confiable que adivinar accountId/userId/ownerId del
        // payload público (ver mapPublicProfile).
        this.verifyOwnerForNavbar(slug, () => this.resolveOwnerCompanyIdAndLoad());
      },
      error: (err) => {
        console.error('Error al cargar el perfil público', err);
        this.resolveOwnerFromSessionAndLoad(slug);
      }
    });
  }

  /**
   * Confirma si quien está logueado es el DUEÑO de este perfil comparando el
   * slug de la URL con el slug propio (mismo endpoint que usa "Compartir"),
   * en vez de depender solo del id que devuelve el payload público (que puede
   * no traer accountId/userId/ownerId y caer en un id de otra entidad). Esta
   * verificación solo decide qué navbar se muestra (isOwnerViewing); el resto
   * del contenido ya se muestra igual con o sin sesión. `done` corre siempre
   * al terminar (haya o no sesión, coincida o no el slug) para no bloquear el
   * resto del flujo mientras se espera esta llamada.
   */
  private verifyOwnerForNavbar(slug: string, done: () => void): void {
    const companyId = this.sessionCompany?.id;
    if (!this.isLoggedIn || !companyId) { done(); return; }
    this.learnApi.shareProfile(companyId).subscribe({
      next: (res) => {
        if (res?.slug && res.slug === slug) { this.profileOwnerId = this.sessionMe.id; }
        done();
      },
      error: () => { done(); }
    });
  }

  /**
   * Si el endpoint público falla (perfil aún no compartido, 404…) el dueño se
   * quedaba con la página vacía. Se confirma la propiedad comparando el slug de
   * la URL con el slug propio del usuario logueado (mismo endpoint que usa
   * "Compartir") y, si coincide, se cargan sus datos autenticados.
   */
  private resolveOwnerFromSessionAndLoad(slug: string): void {
    const companyId = this.sessionCompany?.id;
    if (!this.isLoggedIn || !companyId) { return; }
    this.learnApi.shareProfile(companyId).subscribe({
      next: (res) => {
        if (res?.slug && res.slug === slug) {
          this.profileOwnerId = this.sessionMe.id;
          this.grikySkillsEnabled = !!(this.sessionCompany as any)?.permissions?.grikySkillsModule;
          // Aquí SÍ es correcto usar sessionCompany.id: el slug se generó justo
          // con esta company (shareProfile(companyId)), así que es la dueña.
          this.ownerCompanyId = companyId;
          this.loadOwnAuthenticatedDataIfOwnerViewing();
        }
      },
      error: () => {}
    });
  }

  /**
   * Resuelve qué company id usar para las llamadas autenticadas del dueño. El
   * usuario puede estar logueado con OTRA company activa en sesión (cambió de
   * empresa después de compartir este perfil, o entra desde un link viejo); si
   * usáramos sessionCompany.id a ciegas, el backend scopeaba experiencias/
   * skills/logros a la company equivocada y todo volvía vacío aunque el
   * endpoint público sí tuviera datos completos (companySlug lo confirma).
   */
  private resolveOwnerCompanyIdAndLoad(): void {
    if (!this.isOwnerViewing) {
      console.warn('[PublicProfile] no se cargan datos autenticados: isOwnerViewing=false',
        { sessionMeId: this.sessionMe?.id, profileOwnerId: this.profileOwnerId });
      return;
    }
    const profileSlug = this.company?.slug;
    if (!profileSlug || profileSlug === this.sessionCompany?.slug) {
      this.ownerCompanyId = this.sessionCompany?.id ?? null;
      // Entrada directa a /profile/:slug: no hay CompanyResolver y el storage es
      // en memoria, así que sessionCompany puede ser null. Sin este fallback
      // ownerCompanyId quedaba en null y la guarda de loadOwn… cortaba TODAS las
      // cargas (trayectoria, competencias, logros) y también el refresco del header.
      if (!this.ownerCompanyId) {
        this.resolveCompanyFromAccount();
        return;
      }
      this.loadOwnAuthenticatedDataIfOwnerViewing();
      return;
    }
    this.companyProvider.getCompany(profileSlug, { includes: 'permissions' }).subscribe({
      next: (companyData: any) => {
        this.ownerCompanyId = companyData?.id ?? this.sessionCompany?.id ?? null;
        if (companyData?.permissions) {
          this.grikySkillsEnabled = this.grikySkillsEnabled || !!companyData.permissions.grikySkillsModule;
        }
        this.loadOwnAuthenticatedDataIfOwnerViewing();
      },
      error: () => {
        // No se pudo resolver la company del perfil: se mantiene lo que trajo
        // el endpoint público en vez de arriesgar una llamada con la company
        // equivocada.
        this.ownerCompanyId = this.sessionCompany?.id ?? null;
      }
    });
  }

  /**
   * Último recurso cuando no hay company en sesión ni companySlug en el payload
   * público: se pide /me/companies y se toma la del perfil (o la primera). Sin
   * esto, el dueño que abre su propio link en una pestaña nueva se queda sin
   * ownerCompanyId y la página no dispara ninguna llamada autenticada.
   */
  private resolveCompanyFromAccount(): void {
    this.userProvider.getCompanies({ includes: 'permissions,experience' }).subscribe({
      next: (companies: any[]) => {
        const list = companies || [];
        const match = list.find((c: any) => c?.slug === this.company?.slug) || list[0];
        if (!match?.id) {
          console.warn('[PublicProfile] /me/companies no devolvió ninguna company usable');
          return;
        }
        this.ownerCompanyId = match.id;
        this.sessionCompany = match;
        this.storageService.setLocal('company', match);
        this.grikySkillsEnabled = this.grikySkillsEnabled || !!(match as any)?.permissions?.grikySkillsModule;
        this.loadOwnAuthenticatedDataIfOwnerViewing();
      },
      error: (err) => { console.error('[PublicProfile] error resolviendo la company del dueño', err); }
    });
  }

  /**
   * Trae datos autenticados propios del dueño (contenido/certificados/skills/
   * logros/objetivo/gap-analysis) SOLO cuando quien mira es el propio dueño
   * logueado (isOwnerViewing). Para cualquier otro visitante (anónimo o
   * logueado en otra cuenta/company) esos datos ya vienen del endpoint público
   * en mapPublicProfile.
   */
  private loadOwnAuthenticatedDataIfOwnerViewing(): void {
    if (!this.isOwnerViewing || !this.ownerCompanyId) {
      console.warn('[PublicProfile] carga autenticada omitida',
        { isOwnerViewing: this.isOwnerViewing, ownerCompanyId: this.ownerCompanyId });
      return;
    }
    console.log('[PublicProfile] cargando datos autenticados con companyId', this.ownerCompanyId);
    // Primero el header: iba de último y cualquier excepción en las cargas de
    // datos lo dejaba sin company/access (logo roto, menú y roles vacíos).
    this.refreshOwnCompanyPermissions();
    this.getContent();
    this.getAcademicProfile();
    // Trayectoria y certificaciones externas: el endpoint público no las devuelve,
    // así que para el dueño se piden a los mismos endpoints que usa academy-page.
    // Sin esto las pestañas Trayectoria/Credenciales nunca aparecían.
    this.loadExperiences();
    this.loadEducation();
    this.loadExternalCertifications();
    // Competencias y logros: academy-page los pide SIEMPRE y deja que el backend
    // responda. Condicionarlos a grikySkillsEnabled dejaba las pestañas
    // Competencias y Reconocimientos vacías cuando el endpoint público no manda
    // el bloque `skills`; cada método ya captura su propio error.
    this.getAcquiredSkills();
    this.getAchievements();
    this.getObjectiveData();
    this.getGapAnalysisData();
  }

  /**
   * La ruta 'profile/:slug' no pasa por CompanyResolver (ese :slug es del PERFIL,
   * no de la empresa), así que el 'company'/'access' en StorageService puede venir
   * incompleto (sin permissions.academicProfileModule ni roles) si el dueño entró
   * directo a su perfil público sin haber navegado antes por /learn/:slug en esta
   * sesión. Se refrescan aquí para que el navbar (msc-header) muestre bien el item
   * "Ir a mi perfil académico" y el selector de roles.
   */
  private refreshOwnCompanyPermissions(): void {
    // Entrando directo a /profile/:slug el 'company' de sesión puede ser null
    // (ruta sin CompanyResolver + storage en memoria). En ese caso se usa el slug
    // de la company DUEÑA del perfil, o el header queda sin logo, sin menú y sin roles.
    const slug = this.sessionCompany?.slug || this.company?.slug;
    if (!slug) { return; }
    this.companyProvider.getCompany(slug, { includes: 'experience,permissions' }).subscribe({
      next: (company: any) => {
        this.sessionCompany = company;
        this.storageService.setLocal('company', company);
      },
      error: () => {}
    });
    this.companyProvider.getRoles(slug).subscribe({
      next: (access: any) => {
        this.sessionAccess = access;
        this.storageService.setLocal('access', access);
      },
      error: () => {}
    });
  }

  private mergeList(current: any[], incoming: any[] | null | undefined): any[] {
    return (incoming && incoming.length) ? incoming : current;
  }

  // ===== Habilidades y logros reales (mismo origen que academy/public) =====
  public getAcquiredSkills(): void {
    this.grikySkillsService.getUserSkills(this.ownerCompanyId).subscribe({
      next: (skills) => { this.acquiredSkills = this.mergeList(this.acquiredSkills, skills); },
      error: (err) => { console.error('Error al cargar habilidades', err); }
    });
  }

  public getAchievements(): void {
    this.grikySkillsService.getAchievements(this.ownerCompanyId).subscribe({
      next: (res: GrikyAchievementsResult) => {
        this.achievements = this.mergeList(this.achievements, res?.achievements);
        this.userLevel = res?.level?.level ?? this.userLevel;
        this.userXp = res?.xp ?? this.userXp;
        this.streak = (res as any)?.streak ?? (res as any)?.stats?.currentStreak ?? (res as any)?.currentStreak ?? this.streak;
      },
      error: (err) => { console.error('Error al cargar los logros', err); }
    });
  }

  // Rol objetivo (módulo griky-skills), para "Formándose hacia" en la tarjeta "Sobre mí".
  public objectiveRole: string = '—';
  public getObjectiveData(): void {
    this.grikySkillsService.getObjective(this.ownerCompanyId).subscribe({
      next: (obj: any) => { if (obj?.targetRole) { this.objectiveRole = obj.targetRole; } },
      error: () => { }
    });
  }

  /** Carga el análisis de brechas (módulo griky-skills) para "Mis Competencias" (igual que academy-page). */
  public getGapAnalysisData(): void {
    this.grikySkillsService.getGapAnalysis(this.ownerCompanyId).subscribe({
      next: (data) => { if (data) { this.gapAnalysis = data; } },
      error: () => { }
    });
  }

  public get matchPercent(): number | null {
    const requirements = this.gapAnalysis?.requirements;
    if (this.gapAnalysis?.status !== 'ok' || !requirements || requirements.length === 0) {
      return null;
    }
    const avgRatio = requirements.reduce((sum, r) => {
      const ratio = r.recommendedLevel > 0 ? Math.min(r.currentLevel / r.recommendedLevel, 1) : 1;
      return sum + ratio;
    }, 0) / requirements.length;
    return Math.round(avgRatio * 100);
  }

  public get missingCompetencies(): number | null {
    const requirements = this.gapAnalysis?.requirements;
    if (this.gapAnalysis?.status !== 'ok' || !requirements || requirements.length === 0) {
      return null;
    }
    return requirements.filter(r => r.currentLevel < r.recommendedLevel).length;
  }


  /** Insignia "Verificado por Griky": el usuario tiene al menos un certificado real obtenido en plataforma. */
  public get hasVerifiedBadge(): boolean {
    return this.certificates.length > 0;
  }

  public isZeroMetric(value: number | null | undefined): boolean {
    return value === 0 || value == null;
  }

  public metricText(value: number | null | undefined): string {
    return this.isZeroMetric(value) ? '—' : String(value);
  }

  /** Horas totales de formación, sumando el tiempo real de actividad (eventDuration) de todos los contenidos. */
  public get totalFormationHours(): number {
    const totalSeconds = (this.allContents || []).reduce(
      (sum: number, c: any) => sum + (c?.data?.eventDuration || 0), 0
    );
    return Math.floor(totalSeconds / 3600);
  }

  // ===== Compartir (popover redes + copiar enlace) =====
  public showShareMenu = false;
  public shareUrl = '';
  public linkCopied = false;

  public toggleShare(): void {
    this.showShareMenu = !this.showShareMenu;
    if (this.showShareMenu && !this.shareUrl) { this.buildShareUrl(); }
  }
  public closeShare(): void { this.showShareMenu = false; }

  /** Menú de "..." del navbar móvil (con la opción "Compartir"). */
  public showMobileHeaderMenu = false;
  public toggleMobileHeaderMenu(event: Event): void {
    event.stopPropagation();
    this.showMobileHeaderMenu = !this.showMobileHeaderMenu;
  }
  @HostListener('document:click')
  private closeMobileHeaderMenu(): void {
    this.showMobileHeaderMenu = false;
  }

  /**
   * URL pública del perfil (origin/profile/{slug}), independiente de GrikySkills.
   * Si no hay sesión, usa la URL actual.
   */
  private buildShareUrl(): void {
    const origin = window.location.origin;
    const companyId = this.sessionCompany?.id;
    if (companyId) {
      this.learnApi.shareProfile(companyId).subscribe({
        next: (res) => { this.shareUrl = `${origin}/profile/${res?.slug || ''}`; },
        error: () => { this.shareUrl = window.location.href; }
      });
    } else {
      this.shareUrl = window.location.href;
    }
  }

  public get shareFacebook(): string { return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(this.shareUrl)}`; }
  public get shareTwitter(): string { return `https://twitter.com/intent/tweet?url=${encodeURIComponent(this.shareUrl)}&text=${encodeURIComponent('Mira este perfil')}`; }
  public get shareLinkedIn(): string { return `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(this.shareUrl)}`; }
  public get shareEmail(): string { return `mailto:?subject=${encodeURIComponent('Mira este perfil')}&body=${encodeURIComponent(this.shareUrl)}`; }

  public copyLink(): void {
    if (!this.shareUrl) { return; }
    const nav: any = window.navigator;
    if (nav.clipboard) {
      nav.clipboard.writeText(this.shareUrl).then(() => {
        this.linkCopied = true;
        setTimeout(() => { this.linkCopied = false; }, 2000);
      }).catch(() => { });
    }
  }

  // ===== Certificados obtenidos (datos reales) =====
  public getAcademicProfile(): void {
    if (!this.ownerCompanyId) { return; }
    this.learnApi.getAcademicProfileResume(this.ownerCompanyId).subscribe({
      next: (data: AcademicProfileResume) => {
        this.academicProfile = data;
        this.buildCertificates();
      },
      error: (err) => console.error('Error al cargar el resumen académico', err)
    });
  }

  // ===== Mi contenido (contadores reales, MISMA lógica que academy) =====
  public getContent(): void {
    const params: any = { company_id: this.ownerCompanyId, per_page: 1000, page: 1 };
    this.contentProvider.getContents(params).subscribe((data) => {
      this.allContents = data.contents || [];
      this.computeStatusCounts();
    });
  }

  private computeStatusCounts(): void {
    const list = this.allContents || [];
    let completed = 0, inProgress = 0, notStarted = 0;
    for (const c of list) {
      const p = c?.data?.progress ?? 0;
      if (p >= 100) { completed++; }
      else if (p > 0) { inProgress++; }
      else { notStarted++; }
    }
    this.statusCounts = { completed, inProgress, notStarted, total: list.length };
  }

  private buildCertificates(): void {
    const data = this.academicProfile?.data;
    // Sin datos académicos NO se borran los certificados que ya trajo el endpoint
    // público (ver mergeList): estando logueado se perdían.
    if (!data) { return; }

    // certificatesObtained sale de course_progress_histories: incluye certificados de contenido
    // que ya no está en el catálogo del alumno, que las listas de inscritos no traen.
    const built = (data.certificatesObtained?.data || [])
      .filter((item: any) => item?.certificatePath && item.certificatePath.trim() !== '')
      .map((item: any) => ({
        contentName: item.contentName || '',
        providerName: item.providerName || '',
        certificatePath: item.certificatePath,
        certificatedAt: item.certificatedAt || '',
        certificatedDate: item.certificatedAt ? new Date(item.certificatedAt.replace(' ', 'T')) : null,
        image: this.buildCertificateImage(item.contentImage, item.contentId),
        providerLogo: this.buildProviderLogo(item)
      }))
      .sort((a: any, b: any) => (b.certificatedAt || '').localeCompare(a.certificatedAt || ''));

    this.certificates = this.mergeList(this.certificates, built);
    this.certPage = 0;
  }

  private buildCertificateImage(image: string | null, id: number = 0): string {
    if (!image) {
      const normalizedId = (id || 0) % 7;
      return `https://static.myskillcamp.com/lxp/images/logo-empty-state-${normalizedId}.webp`;
    }
    let url = image.startsWith('http') ? image : `${environment.envVar.API_URL}/stockage/${image}`;
    if (!environment.production) {
      url = url.replace('https://api-upgraded.myskillcamp.com.deltabluecdn.com', 'https://api.myskillcamp.com');
    }
    return url.includes('?') ? url : `${url}?size=512`;
  }

  ngAfterViewInit(): void {
    this.checkBioOverflow();
  }

  /**
   * Vuelca los datos del endpoint público en las variables del diseño. Solo
   * sobreescribe lo que venga; el resto conserva el placeholder.
   */
  private mapPublicProfile(data: any): void {
    if (!data) { return; }
    // El endpoint puede responder plano o envuelto en `data` — sin desenvolverlo
    // todos los campos quedaban undefined y la vista se veía vacía.
    const p = (data.data && typeof data.data === 'object' && !Array.isArray(data.data)) ? data.data : data;
    this.profileOwnerId = p.accountId ?? p.userId ?? p.ownerId ?? p.user?.id ?? p.account?.id ?? p.id ?? null;
    this.me = {
      ...this.me,
      name: p.userName || p.name || this.me.name,
      firstname: p.firstname || p.userName || p.name || this.me.firstname,
      lastname: p.lastname || this.me.lastname,
      picture: p.picture || p.avatar || p.userPicture || this.me.picture,
      biography: p.biography || p.bio || p.about || this.me.biography,
      headline: p.headline || p.role || this.me.headline,
      city: p.city || this.me.city
    };
    if (p.companyName || p.companySlug) {
      this.company = { name: p.companyName || 'Griky', slug: p.companySlug || '', experience: {} };
    }
    // Para un visitante externo, la fuente de verdad es el endpoint público: solo
    // manda `skills` si la company DUEÑA tiene griky_skills_module activo.
    // Cuando el que mira es el propio dueño, la company de sesión ES la del perfil,
    // así que usamos su permiso igual que academy-page — si no, el endpoint público
    // (que no manda `skills`) dejaba el flag en false y las pestañas Competencias y
    // Reconocimientos no aparecían nunca.
    this.grikySkillsEnabled = Array.isArray(p.skills)
      || (this.isOwnerViewing && !!(this.sessionCompany as any)?.permissions?.grikySkillsModule);
    if (Array.isArray(p.skills)) { this.acquiredSkills = p.skills; }
    if (Array.isArray(p.achievements)) { this.achievements = p.achievements; }
    if (p.level) { this.userLevel = p.level.level ?? this.userLevel; }
    if (typeof p.xp === 'number') { this.userXp = p.xp; }
    if (Array.isArray(p.certificates)) {
      this.certificates = p.certificates.map((c: any) => ({
        contentName: c.contentName || c.name || '',
        providerName: c.providerName || '',
        certificatedDate: c.certificatedAt ? new Date(String(c.certificatedAt).replace(' ', 'T')) : (c.certificatedDate || null),
        providerLogo: c.providerLogo || this.buildProviderLogo(c)
      }));
      this.certPage = 0;
    }
    if (Array.isArray(p.experiences)) { this.experiences = p.experiences.map((e: any) => this.mapExperience(e)); }
    if (Array.isArray(p.education)) { this.education = p.education.map((e: any) => this.mapEducation(e)); }
    if (Array.isArray(p.externalCertifications)) {
      this.externalCerts = p.externalCertifications.map((c: any) => this.mapCertification(c));
    }
    if (p.targetRole) { this.objectiveRole = p.targetRole; }
    if (p.memberSince) { this.memberSince = this.formatMemberSince(p.memberSince); }
    if (p.lastActiveAt) { this.lastActiveLabel = this.formatRelativeTime(p.lastActiveAt); }
    if (p.stats) {
      this.statusCounts = {
        total: p.stats.total ?? this.statusCounts.total,
        inProgress: p.stats.inProgress ?? this.statusCounts.inProgress,
        completed: p.stats.completed ?? this.statusCounts.completed,
        notStarted: p.stats.notStarted ?? 0
      };
      if (typeof p.stats.currentStreak === 'number') { this.streak = p.stats.currentStreak; }
    }
    this.checkBioOverflow();

    // "En Griky desde": si el endpoint público no lo trae, pero el DUEÑO está viendo
    // su propio perfil logueado, usamos el mismo dato real que Editar perfil > Licencia
    // (companyUser.accessStatus.accessStartAt / subscriptionDate).
    if (!this.memberSince && this.isOwnerViewing && this.sessionCompany?.id) {
      this.userProvider.getAccount(this.sessionCompany.id).subscribe({
        next: (account: any) => {
          const since = account?.companyUser?.accessStatus?.accessStartAt || account?.companyUser?.subscriptionDate;
          if (since) { this.memberSince = this.formatMemberSince(since); }
        },
        error: () => {}
      });
    }
  }

  private checkBioOverflow(): void {
    setTimeout(() => {
      const el = this.bioText?.nativeElement;
      if (!el) { this.bioOverflowing = false; return; }
      this.bioOverflowing = el.scrollHeight > el.clientHeight + 1;
    });
  }

  get hasUploadedPicture(): boolean {
    return !!(this.me && this.me.picture && this.me.picture.indexOf('ui-avatars.com') === -1);
  }

  get shortName(): string {
    const first = (this.me?.firstname || '').trim().split(/\s+/)[0] || '';
    const last = (this.me?.lastname || '').trim().split(/\s+/)[0] || '';
    return (first + ' ' + last).trim() || (this.me?.name || '');
  }

  get role(): string {
    return this.me?.headline || '';
  }

  get subtitle(): string {
    const headline = (this.me?.headline || '').trim();
    if (headline) { return headline; }
    const company = this.company?.name || this.company?.experience?.name || '';
    return `Estudiante de ${company} · ${this.currentYear}`;
  }

  // ===== Certificados: logo del proveedor =====
  private buildProviderLogo(item: any): string {
    const slug = (item?.providerSlug || item?.providerName || '')
      .toString().trim().toLowerCase().replace(/\s+/g, '-');
    if (!slug) { return this.fallbackProviderLogo; }
    return `https://static.myskillcamp.com/images/integrations/${slug}.svg`;
  }
  private get fallbackProviderLogo(): string {
    return 'https://webservices.griky.co/cdn/img/Griky-Logotipo.webp';
  }
  public onLogoError(event: Event): void {
    const img = event.target as HTMLImageElement;
    const fallback = this.fallbackProviderLogo;
    if (img.src !== fallback) { img.src = fallback; }
  }

  // ===== Logros =====
  public get unlockedAchievements(): GrikyAchievement[] {
    return (this.achievements || []).filter((a) => a.isUnlocked);
  }
  public badgeUrl(icon: string): string {
    return `assets/badges/pack-achievements/es_ES/${icon}.png`;
  }

  // ===== Nav del sidebar: pestañas reales (mismo patrón que academy-page: activeTab / setTab / showSection) =====
  public activeTab: string = 'resumen';
  public get isResumen(): boolean {
    return this.activeTab === 'resumen';
  }
  public setTab(tab: string): void {
    this.activeTab = tab;
  }
  public showSection(section: string): boolean {
    return this.isResumen || this.activeTab === section;
  }

  // ===== Carrusel de "Últimos certificados" (mismo patrón que academy-page) =====
  public certPage: number = 0;
  public certPerPage: number = 4;

  /**
   * Ítems del carrusel de certificados. La tarjeta "Sigue aprendiendo" es
   * propia de academy-page (perfil privado); esta es la vista pública del
   * perfil (/profile/:slug), la vea el dueño logueado o un visitante, así
   * que nunca se agrega aquí.
   */
  private get certItems(): any[] {
    return [...this.certificates];
  }

  /** Todos los ítems del carrusel (usado por el slider táctil en móvil). */
  public get allCertItems(): any[] {
    return this.certItems;
  }

  public get certTotalPages(): number {
    return Math.max(1, Math.ceil(this.certItems.length / this.certPerPage));
  }
  public get pagedCertItems(): any[] {
    const start = this.certPage * this.certPerPage;
    return this.certItems.slice(start, start + this.certPerPage);
  }
  public certPrev(): void {
    if (this.certPage > 0) { this.certPage--; }
  }
  public certNext(): void {
    if (this.certPage < this.certTotalPages - 1) { this.certPage++; }
  }
  public goCertPage(index: number): void {
    if (index >= 0 && index < this.certTotalPages) { this.certPage = index; }
  }

  // ===========================================================================
  // Modales "Agregar" — presentes por compartir plantilla con academy/public,
  // pero como isOwner=false nunca se abren en el perfil público.
  // ===========================================================================
  public showExperienceModal = false;
  public showEducationModal = false;
  public showCertificationModal = false;

  public companySelected = false;
  public universitySelected = false;
  public issuerSelected = false;

  public companyOptions: string[] = ['Griky', 'Griyid', 'Innovate Corp.', 'TechStart', 'CodeLab', 'Google', 'Microsoft', 'Amazon', 'Platzi', 'Netzun'];
  public countryOptions: string[] = ['Colombia', 'México', 'Argentina', 'Perú', 'Chile', 'España', 'Estados Unidos'];
  public universitiesByCountry: { [country: string]: Array<{ name: string; city: string }> } = {
    'Colombia': [
      { name: 'Universidad Nacional de Colombia', city: 'Bogotá' },
      { name: 'Universidad de los Andes', city: 'Bogotá' },
      { name: 'Pontificia Universidad Javeriana', city: 'Bogotá' },
      { name: 'Universidad de Antioquia', city: 'Medellín' },
      { name: 'Universidad del Valle', city: 'Cali' }
    ],
    'México': [
      { name: 'Universidad Nacional Autónoma de México', city: 'Ciudad de México' },
      { name: 'Instituto Politécnico Nacional', city: 'Ciudad de México' },
      { name: 'Tecnológico de Monterrey', city: 'Monterrey' }
    ],
    'Argentina': [
      { name: 'Universidad de Buenos Aires', city: 'Buenos Aires' },
      { name: 'Universidad Nacional de Córdoba', city: 'Córdoba' }
    ],
    'Perú': [
      { name: 'Universidad Nacional Mayor de San Marcos', city: 'Lima' },
      { name: 'Pontificia Universidad Católica del Perú', city: 'Lima' }
    ],
    'Chile': [
      { name: 'Universidad de Chile', city: 'Santiago' },
      { name: 'Pontificia Universidad Católica de Chile', city: 'Santiago' }
    ],
    'España': [
      { name: 'Universidad Complutense de Madrid', city: 'Madrid' },
      { name: 'Universidad de Barcelona', city: 'Barcelona' }
    ],
    'Estados Unidos': [
      { name: 'Massachusetts Institute of Technology', city: 'Cambridge' },
      { name: 'Stanford University', city: 'Stanford' }
    ]
  };
  public degreeOptions: string[] = ['Técnico', 'Tecnólogo', 'Pregrado/BSc', 'Especialización', 'Maestría/MSc', 'Doctorado/PhD'];
  public studyAreaSuggestions: string[] = ['Ingeniería de Software', 'Ingeniería de Sistemas', 'Administración de Empresas', 'Diseño Gráfico', 'Marketing Digital', 'Ciencia de Datos', 'Contaduría'];
  public issuerOptions: Array<{ name: string; logo?: string }> = [
    { name: 'Meta', logo: 'https://cdn.simpleicons.org/meta/0081FB' },
    { name: 'Google', logo: 'https://cdn.simpleicons.org/google/4285F4' },
    { name: 'AWS', logo: 'https://cdn.simpleicons.org/amazonwebservices/232F3E' },
    { name: 'Coursera', logo: 'https://cdn.simpleicons.org/coursera/0056D2' },
    { name: 'Microsoft', logo: 'https://cdn.simpleicons.org/microsoft/5E5E5E' },
    { name: 'Platzi', logo: 'https://cdn.simpleicons.org/platzi/98CA3F' }
  ];

  public months = [
    { v: 1, l: 'Ene' }, { v: 2, l: 'Feb' }, { v: 3, l: 'Mar' }, { v: 4, l: 'Abr' },
    { v: 5, l: 'May' }, { v: 6, l: 'Jun' }, { v: 7, l: 'Jul' }, { v: 8, l: 'Ago' },
    { v: 9, l: 'Sep' }, { v: 10, l: 'Oct' }, { v: 11, l: 'Nov' }, { v: 12, l: 'Dic' }
  ];
  public years: number[] = [];

  public expForm: any = this.emptyExpForm();
  public eduForm: any = this.emptyEduForm();
  public certForm: any = this.emptyCertForm();

  private emptyExpForm(): any {
    return { title: '', company: '', companyManual: false, startMonth: null, startYear: null, endMonth: null, endYear: null, current: false, description: '' };
  }
  private emptyEduForm(): any {
    return { country: 'Colombia', university: '', universityManual: false, degree: '', area: '', startMonth: null, startYear: null, endMonth: null, endYear: null };
  }
  private emptyCertForm(): any {
    return { name: '', issuer: '', issuerLogo: '', issuerManual: false, issueMonth: null, issueYear: null, expMonth: null, expYear: null, url: '' };
  }

  private buildYears(): void {
    const currentYear = new Date().getFullYear();
    this.years = [];
    for (let y = currentYear; y >= 1975; y--) { this.years.push(y); }
  }

  public openExperienceModal(): void { this.expForm = this.emptyExpForm(); this.companySelected = false; this.showExperienceModal = true; }
  public openEducationModal(): void { this.eduForm = this.emptyEduForm(); this.universitySelected = false; this.showEducationModal = true; }
  public openCertificationModal(): void { this.certForm = this.emptyCertForm(); this.issuerSelected = false; this.showCertificationModal = true; }
  public closeModals(): void { this.showExperienceModal = false; this.showEducationModal = false; this.showCertificationModal = false; }

  public get filteredCompanies(): string[] {
    const q = (this.expForm.company || '').toLowerCase().trim();
    const list = q ? this.companyOptions.filter(c => c.toLowerCase().includes(q)) : this.companyOptions;
    return list.slice(0, 6);
  }
  public get showCompanySuggestions(): boolean {
    return !!this.expForm.company && !this.expForm.companyManual && !this.companySelected;
  }
  public onCompanyInput(): void { this.companySelected = false; }
  public selectCompany(name: string): void { this.expForm.company = name; this.companySelected = true; }
  public enableManualCompany(): void { this.expForm.companyManual = true; this.companySelected = true; }

  public get filteredUniversities(): Array<{ name: string; city: string }> {
    const list = this.universitiesByCountry[this.eduForm.country] || [];
    const q = (this.eduForm.university || '').toLowerCase().trim();
    const filtered = q ? list.filter(u => u.name.toLowerCase().includes(q)) : list;
    return filtered.slice(0, 6);
  }
  public get showUniversitySuggestions(): boolean {
    return !!this.eduForm.country && !!this.eduForm.university && !this.eduForm.universityManual && !this.universitySelected;
  }
  public onUniversityInput(): void { this.universitySelected = false; }
  public onCountryChange(): void { this.eduForm.university = ''; this.universitySelected = false; }
  public selectUniversity(u: { name: string; city: string }): void { this.eduForm.university = u.name; this.universitySelected = true; }
  public enableManualUniversity(): void { this.eduForm.universityManual = true; this.universitySelected = true; }

  public get filteredIssuers(): Array<{ name: string; logo?: string }> {
    const q = (this.certForm.issuer || '').toLowerCase().trim();
    const list = q ? this.issuerOptions.filter(i => i.name.toLowerCase().includes(q)) : this.issuerOptions;
    return list.slice(0, 6);
  }
  public get showIssuerSuggestions(): boolean {
    return !!this.certForm.issuer && !this.certForm.issuerManual && !this.issuerSelected;
  }
  public onIssuerInput(): void { this.issuerSelected = false; this.certForm.issuerLogo = ''; }
  public selectIssuer(i: { name: string; logo?: string }): void { this.certForm.issuer = i.name; this.certForm.issuerLogo = i.logo || ''; this.issuerSelected = true; }
  public enableManualIssuer(): void { this.certForm.issuerManual = true; this.issuerSelected = true; }

  private monthLabel(m: any): string {
    const found = this.months.find(x => x.v === +m);
    return found ? found.l : '';
  }
  private formatMonthYear(m: any, y: any): string {
    if (!y) { return ''; }
    const ml = this.monthLabel(m);
    return ml ? `${ml} ${y}` : `${y}`;
  }
  private formatPeriod(sM: any, sY: any, eM: any, eY: any, current: boolean): string {
    const start = this.formatMonthYear(sM, sY);
    const end = current ? 'Actualidad' : this.formatMonthYear(eM, eY);
    return [start, end].filter(Boolean).join(' - ');
  }

  // --- Mapeo de la respuesta estructurada del perfil público al shape de display del HTML ---
  /** Experiencia laboral real del dueño (mismo endpoint que academy-page). */
  private loadExperiences(): void {
    this.learnApi.getExperiences(this.ownerCompanyId).subscribe({
      next: (data: any) => { this.experiences = this.mergeList(this.experiences, (data || []).map((e: any) => this.mapExperience(e))); },
      error: (err: any) => { console.error('Error al cargar la experiencia laboral', err); }
    });
  }

  /** Educación real del dueño (mismo endpoint que academy-page). */
  private loadEducation(): void {
    this.learnApi.getEducation(this.ownerCompanyId).subscribe({
      next: (data: any) => { this.education = this.mergeList(this.education, (data || []).map((e: any) => this.mapEducation(e))); },
      error: (err: any) => { console.error('Error al cargar la educación', err); }
    });
  }

  /** Certificaciones externas reales del dueño (mismo endpoint que academy-page). */
  private loadExternalCertifications(): void {
    this.learnApi.getExternalCertifications(this.ownerCompanyId).subscribe({
      next: (data: any) => { this.externalCerts = this.mergeList(this.externalCerts, (data || []).map((c: any) => this.mapCertification(c))); },
      error: (err: any) => { console.error('Error al cargar las certificaciones externas', err); }
    });
  }

  private mapExperience(e: any): any {
    return {
      title: e.companyName ? `${e.title} en ${e.companyName}` : e.title,
      period: this.formatPeriod(e.startMonth, e.startYear, e.endMonth, e.endYear, e.isCurrent),
      current: e.isCurrent,
      startYear: e.startYear
    };
  }

  private mapEducation(e: any): any {
    return {
      university: e.university,
      career: [e.degree, e.studyArea].filter(Boolean).join(' '),
      period: this.formatPeriod(e.startMonth, e.startYear, e.endMonth, e.endYear, false),
      startYear: e.startYear
    };
  }

  private mapCertification(c: any): any {
    return {
      title: c.name,
      org: c.issuer,
      logo: c.issuerLogo || undefined,
      date: this.formatMonthYear(c.issueMonth, c.issueYear),
      issueYear: c.issueYear
    };
  }

  public saveExperience(): void {
    const title = this.expForm.company ? `${this.expForm.title} en ${this.expForm.company}` : this.expForm.title;
    this.experiences.unshift({
      title,
      period: this.formatPeriod(this.expForm.startMonth, this.expForm.startYear, this.expForm.endMonth, this.expForm.endYear, this.expForm.current),
      current: this.expForm.current,
      startYear: this.expForm.startYear
    });
    this.closeModals();
  }
  public saveEducation(): void {
    const career = [this.eduForm.degree, this.eduForm.area].filter(Boolean).join(' ');
    this.education.unshift({
      university: this.eduForm.university,
      career: career,
      period: this.formatPeriod(this.eduForm.startMonth, this.eduForm.startYear, this.eduForm.endMonth, this.eduForm.endYear, false),
      startYear: this.eduForm.startYear
    });
    this.closeModals();
  }
  public saveCertification(): void {
    this.externalCerts.unshift({
      title: this.certForm.name,
      org: this.certForm.issuer,
      date: this.formatMonthYear(this.certForm.issueMonth, this.certForm.issueYear),
      logo: this.certForm.issuerLogo || undefined,
      issueYear: this.certForm.issueYear
    });
    this.closeModals();
  }
}
